import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Users, CheckCircle, Hourglass, Coins, MapPin, ArrowRight } from "@phosphor-icons/react";

function Kpi({ icon: Icon, label, value, accent }) {
    return (
        <div className="bento-card fade-up">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-ink-soft">{label}</p>
                    <p className="font-display text-3xl font-bold text-ink mt-3">{value}</p>
                </div>
                <div
                    className="w-10 h-10 rounded-md grid place-items-center"
                    style={{ background: accent + "1a", color: accent }}
                >
                    <Icon size={22} weight="duotone" />
                </div>
            </div>
        </div>
    );
}

export default function Dashboard() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/dashboard/stats").then((r) => setStats(r.data));
    }, []);

    if (!stats)
        return (
            <Layout>
                <div className="text-ink-soft">Chargement…</div>
            </Layout>
        );

    return (
        <Layout>
            <div className="mb-8">
                <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-2">Tableau de bord</p>
                <h1 className="font-display text-4xl font-bold text-ink">Vue d'ensemble</h1>
                <p className="text-ink-soft mt-2">Suivi global des clients, paiements et cités.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Kpi icon={Users} label="Clients" value={stats.total_clients} accent="#C86B52" />
                <Kpi icon={CheckCircle} label="Soldés" value={stats.fully_paid} accent="#4A7C59" />
                <Kpi icon={Hourglass} label="En cours" value={stats.pending} accent="#E4A853" />
                <Kpi icon={Coins} label="Encaissé" value={formatMoney(stats.total_paid)} accent="#C86B52" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-2 bento-card fade-up">
                    <h3 className="font-display text-xl font-bold text-ink mb-4">Bilan financier</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="p-4 rounded-md bg-cream">
                            <p className="text-xs uppercase tracking-widest text-ink-soft">Prévu</p>
                            <p className="font-display text-2xl text-ink mt-2" data-testid="kpi-expected">
                                {formatMoney(stats.total_expected)}
                            </p>
                        </div>
                        <div className="p-4 rounded-md bg-sage/10">
                            <p className="text-xs uppercase tracking-widest text-sage-dark">Encaissé</p>
                            <p className="font-display text-2xl text-sage-dark mt-2" data-testid="kpi-collected">
                                {formatMoney(stats.total_paid)}
                            </p>
                        </div>
                        <div className="p-4 rounded-md bg-terracotta/10">
                            <p className="text-xs uppercase tracking-widest text-terracotta-dark">Reste</p>
                            <p className="font-display text-2xl text-terracotta-dark mt-2" data-testid="kpi-remaining">
                                {formatMoney(stats.remaining_global)}
                            </p>
                        </div>
                    </div>
                </Card>

                <Card className="bento-card fade-up">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-xl font-bold text-ink">Par cité</h3>
                        <Link
                            to="/cites"
                            className="text-xs uppercase tracking-widest text-terracotta hover:underline flex items-center gap-1"
                        >
                            Voir <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div className="space-y-2">
                        {stats.cities.length === 0 && (
                            <p className="text-sm text-ink-soft">Aucune cité enregistrée.</p>
                        )}
                        {stats.cities.map((c) => (
                            <div
                                key={c.city}
                                className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-cream"
                                data-testid={`city-row-${c.city}`}
                            >
                                <div className="flex items-center gap-2">
                                    <MapPin size={16} className="text-terracotta" />
                                    <span className="text-sm text-ink">{c.city}</span>
                                </div>
                                <span className="text-sm font-semibold text-ink">{c.count}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <div className="mt-6 flex gap-3">
                <Link
                    to="/clients/new"
                    className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-5 py-3 rounded-md font-medium active:translate-y-[1px] transition-all"
                    data-testid="new-client-cta"
                >
                    + Nouveau client
                </Link>
                <Link
                    to="/clients"
                    className="inline-flex items-center gap-2 bg-sand hover:bg-sand/70 text-ink px-5 py-3 rounded-md font-medium transition-all"
                    data-testid="view-clients-cta"
                >
                    Voir tous les clients
                </Link>
            </div>
        </Layout>
    );
}
